import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Landmark, Save, FileSignature, CheckCircle2, Lock, Unlock, Download, Printer, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { downloadBlob } from "@/lib/download";
import { SepaSheet } from "@/components/SepaSheet";
import { SepaOtpDialog } from "@/components/SepaOtpDialog";
import { DataConsentSignDialog } from "@/components/DataConsentSignDialog";

const EDITABLE = ["debtor_name", "debtor_address", "debtor_cp_city", "debtor_country", "swift_bic", "iban", "payment_type", "signed_city"];
const LABELS = { debtor_name: "Nombre del deudor", debtor_address: "Dirección", debtor_cp_city: "Código postal - Población - Provincia",
    debtor_country: "País", swift_bic: "Swift BIC", iban: "IBAN", signed_city: "Localidad" };
const todayMadrid = () => new Date().toLocaleDateString("es-ES", { timeZone: "Europe/Madrid", day: "2-digit", month: "2-digit", year: "numeric" });
const clean = (v) => (v || "").replace(/\s+/g, "").toUpperCase();
const ibanOk = (v) => {
    const s = clean(v);
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(s) || (s.startsWith("ES") && s.length !== 24)) return false;
    const num = (s.slice(4) + s.slice(0, 4)).split("").map((c) => (/\d/.test(c) ? c : String(c.charCodeAt(0) - 55))).join("");
    let r = 0;
    for (const ch of num) { r = (r * 10 + Number(ch)) % 97; }
    return r === 1;
};
const problems = (d) => {
    const m = EDITABLE.filter((k) => k !== "payment_type" && !(d[k] || "").trim()).map((k) => LABELS[k]);
    if (d.iban && !ibanOk(d.iban)) m.push("IBAN válido");
    if (d.swift_bic && !/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(clean(d.swift_bic))) m.push("Swift BIC válido");
    return m;
};

export default function SepaPanel({ patientId, kiosk = false, apiClient = null, authHeaders = null, onSigned, refreshKey = 0 }) {
    const client = useRef(apiClient || api).current;
    const [doc, setDoc] = useState(null);
    const [busy, setBusy] = useState(false);
    const [signOpen, setSignOpen] = useState(false);
    const [otpOpen, setOtpOpen] = useState(false);
    const [unlock, setUnlock] = useState(null);
    const base = kiosk ? "/kiosk/sepa" : `/patients/${patientId}/sepa`;
    const cfg = useCallback((extra = {}) => ({ ...extra, headers: { ...(authHeaders || {}), ...(unlock ? { "X-Sepa-Unlock": unlock.token } : {}) } }), [authHeaders, unlock]);

    const load = useCallback(async () => {
        try { const { data } = await client.get(base, cfg()); setDoc(data); }
        catch (e) { if (e.response?.status !== 403) toast.error(e.response?.data?.detail || "No se pudo cargar la orden SEPA"); }
    }, [client, base, cfg]);
    useEffect(() => { if (kiosk || patientId) { load(); } }, [load, kiosk, patientId, refreshKey]);
    useEffect(() => {
        if (!unlock) { return; }
        const t = setTimeout(() => { setUnlock(null); toast.message("Datos bancarios ocultos de nuevo"); }, new Date(unlock.expires).getTime() - Date.now());
        return () => clearTimeout(t);
    }, [unlock]);

    if (!doc || (!doc.required && doc.status !== "signed")) return null;
    const isSigned = doc.status === "signed";
    const editable = doc.can_edit && !isSigned;
    const set = (patch) => setDoc((d) => ({ ...d, ...patch }));
    const persist = async () => {
        const { data } = await client.put(base, Object.fromEntries(EDITABLE.map((k) => [k, doc[k] ?? null])), cfg());
        setDoc(data);
    };
    const run = async (fn, okMsg) => {
        setBusy(true);
        try { await fn(); if (okMsg) toast.success(okMsg); }
        catch (e) { toast.error(e.response?.data?.detail || "No se pudo completar la acción"); }
        finally { setBusy(false); }
    };
    const openSign = () => { const m = problems(doc); if (m.length) { toast.error(`Completa: ${m.join(", ")}`); return; } setSignOpen(true); };
    const sign = (payload) => run(async () => {
        await persist();
        const { data } = await client.post(`${base}/sign`, { signature_data_url: payload.signature_data_url }, cfg());
        setDoc(data); setSignOpen(false); onSigned && onSigned(data);
    }, "Orden SEPA firmada y guardada en la ficha");
    const pdfBlob = async () => (await client.get(`${base}/pdf`, cfg({ responseType: "blob" }))).data;
    const download = () => run(async () => downloadBlob(await pdfBlob(), `Orden-SEPA-${doc.reference}.pdf`));
    const printPdf = () => run(async () => {
        const w = window.open(URL.createObjectURL(new Blob([await pdfBlob()], { type: "application/pdf" })), "_blank");
        if (!w) { toast.error("Permite ventanas emergentes para imprimir"); return; }
        setTimeout(() => { try { w.focus(); w.print(); } catch (_e) { } }, 900);
    });
    const reopen = () => {
        if (!window.confirm("¿Reabrir la orden SEPA para volver a firmarla? El PDF firmado seguirá en Documentos.")) return;
        run(async () => { const { data } = await client.post(`${base}/reopen`, {}, cfg()); setDoc(data); }, "Orden SEPA reabierta");
    };
    const until = unlock && new Date(unlock.expires).toLocaleTimeString("es-ES", { timeZone: "Europe/Madrid", hour: "2-digit", minute: "2-digit" });

    return (
        <Card className="p-4 sm:p-5 space-y-4" data-testid="sepa-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="uppercase-label flex items-center gap-1"><Landmark size={12} /> Domiciliación bancaria</div>
                    <div className="font-semibold mt-0.5">Orden SEPA · {doc.reference}</div>
                </div>
                {isSigned ? (
                    <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300" data-testid="sepa-status-signed">
                        <CheckCircle2 size={12} className="mr-1" /> Firmada el {doc.signed_date}{doc.signed_by_kiosk ? " · Quiosco" : ""}
                    </Badge>
                ) : <Badge variant="outline" className="text-amber-800 border-amber-300" data-testid="sepa-status-draft">Pendiente de firma</Badge>}
            </div>
            {doc.masked && (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs" data-testid="sepa-masked-note">
                    <Lock size={13} /> Datos bancarios ocultos.
                    {doc.can_unlock ? " Para verlos se envía una contraseña de un solo uso al email de la clínica." : " Solo dirección y secretaría pueden verlos."}
                    {doc.can_unlock && <Button size="sm" variant="outline" className="ml-auto" onClick={() => setOtpOpen(true)} data-testid="sepa-unlock"><Unlock size={13} className="mr-1" /> Ver datos</Button>}
                </div>
            )}
            {unlock && !doc.masked && <div className="text-xs text-emerald-800" data-testid="sepa-unlocked-note"><Unlock size={12} className="inline mr-1" />Datos visibles hasta las {until} (hora de Madrid).</div>}
            {editable && <p className="text-xs text-muted-foreground">Todos los campos son obligatorios. Los datos de la ficha (o de facturación si el titular es otra persona) ya están rellenados.</p>}
            <div className="overflow-x-auto rounded-md bg-secondary/30 p-2 sm:p-4">
                <SepaSheet doc={doc} set={set} readOnly={!editable} dateLabel={doc.signed_date || todayMadrid()} />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
                {editable && <Button variant="outline" disabled={busy} onClick={() => run(persist, "Datos guardados")} data-testid="sepa-save"><Save size={14} className="mr-1" /> Guardar datos</Button>}
                {editable && <Button className="btn-cta" disabled={busy} onClick={openSign} data-testid="sepa-open-sign"><FileSignature size={14} className="mr-1" /> Firmar orden SEPA</Button>}
                {isSigned && doc.can_unlock && (
                    <>
                        <Button variant="outline" disabled={busy} onClick={unlock ? download : () => setOtpOpen(true)} data-testid="sepa-download"><Download size={14} className="mr-1" /> Descargar PDF</Button>
                        <Button variant="outline" disabled={busy} onClick={unlock ? printPdf : () => setOtpOpen(true)} data-testid="sepa-print"><Printer size={14} className="mr-1" /> Imprimir</Button>
                        <Button variant="ghost" disabled={busy} onClick={unlock ? reopen : () => setOtpOpen(true)} data-testid="sepa-reopen"><RotateCcw size={14} className="mr-1" /> Reabrir</Button>
                    </>
                )}
            </div>
            <DataConsentSignDialog open={signOpen} onOpenChange={setSignOpen} patientName={doc.debtor_name} onSign={sign}
                submitting={busy} kiosk={kiosk} title="Firma del deudor · Orden SEPA" allowRepresentative={false} />
            {!kiosk && <SepaOtpDialog open={otpOpen} onOpenChange={setOtpOpen} patientId={patientId}
                onUnlocked={(token, expires) => { setUnlock({ token, expires }); toast.success("Datos visibles durante 10 minutos"); }} />}
        </Card>
    );
}
